import React, { useState, useRef, useEffect } from 'react';
import { api } from '../api';
import { Send, Bot, User, Loader2, ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const CHAT_SUBTITLES = [
  // originals…
  "Hey, what do you want to know? I promise I won't lie.",
  "Ask me something, portfolio, process, VR, existential dread.",
  "I'm Juan, but digital. What can I help you with?",
  "Curious about my work? I'm contractually obligated to answer.",
  "UX? UI? Game design? Career path? Experience? Hit me.",
  "You've got questions. I've got mildly sarcastic answers.",
  "Ask anything about my projects. Except what I had for lunch.",
  "I've prototyped for VR, web, mobile — and now, this bot.",
  "I don't dream of wireframes. But I do build them.",
  "I'm not ChatGPT. I'm Juan with better word choices.",
  "Freelance, full-time, just snooping? Either way, I'm ready.",
  "You're here. I'm here. Let's make this productive.",
  "I've designed games, dashboards, wallets, ask me anything.",
  "Type something. I'll respond like I really mean it.",
  "This AI runs on prototypes, coffee, and more coffee.",

  // new additions…
  "Shoot a question—design, code, football, life hacks, I'm game.",
  "Looking for insights or hot takes? Step right up, let's talk.",
  "Need a portfolio deep-dive or random advice? I'm all ears.",
  "Small query or big problem, I'll keep it brutally honest.",
  "Ask about VR widgets or cereal packaging, both spark joy.",
  "Design rant, process tip, travel story—pick your adventure here.",
  "Coffee-powered, sarcasm-enabled—fire away with your burning question.",
  "UX myths to bust? Game mechanics to tweak? I'm listening.",
  "Curiosity is cheap. Answers are free. What's on your mind?",
  "From wireframes to world maps, ask and I'll map it out.",
  "Got a weird brief or career dilemma? Let's dissect it.",
  "Want my take on AI, Figma, or soccer teams? Try me.",
  "I turn chaos into prototypes—what chaos can I solve today?",
  "Question me like StackOverflow, I'll reply like it's personal.",
  "Pick a topic: design debt, VR nausea, or leap-year birthdays.",
  "This chat is a sandbox—throw something in and watch me build."
];

// Enhanced Markdown renderer component
const MarkdownRenderer = ({ content }) => (
  <div className="markdown-content">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({node, ...props}) => (
          <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>
        )
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

// Typewriter effect for markdown content
const TypewriterMarkdown = ({ content, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const intervalRef = useRef();

  useEffect(() => {
    let i = 0;
    intervalRef.current = setInterval(() => {
      i++;
      setDisplayed(content.slice(0, i));
      if (i >= content.length) {
        clearInterval(intervalRef.current);
        if (onDone) onDone();
      }
    }, 12); // ~80 chars/sec
    return () => clearInterval(intervalRef.current);
  }, [content, onDone]);

  return <MarkdownRenderer content={displayed} />;
};

function Chat() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [isTypewriting, setIsTypewriting] = useState(false);
  const [lastAnimatedBotId, setLastAnimatedBotId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const [subtitle] = useState(() => CHAT_SUBTITLES[Math.floor(Math.random() * CHAT_SUBTITLES.length)]);
  const animatedBotIds = useRef(new Set());

  const allQuickQuestions = [
    // Professional/Technical
  "What's your biggest project achievement?",
  "Tell me about your tech stack",
  "What's your development workflow?",
  "How do you approach problem-solving?",
  "Do you prototype everything?",
  "What tools do you use every day?",
  "Do you write production code or just prototypes?",
  "How do you hand things off to devs?",
  "What's your take on design systems?",

  // Creative/Design
  "What drives your creativity?",
  "What's your design philosophy?",
  "How do you stay inspired?",
  "What's your favorite project?",
  "Do you start with pen and paper or screen?",
  "What's the first thing you do with a messy brief?",
  "How do you balance aesthetics vs. function?",
  "What part of the process do you enjoy the most?",
  "Where do your ideas start?",
  "Favourite UI pattern?",
  "Font you over-use?",
  "Any design hill you’d die on?",
  "How do you kill feature creep?",
  "Grids: love or loathe?",
  "Sketching or mind-maps?",
  "Who critiques your work first?",
  "Colour palette workflow?",
  "How do you pick type?",

  // Portfolio/Projects
  "Walk me through a project",
  "What problems do you solve?",
  "Show me your best work",
  "What's your project process?",
  "What's the weirdest thing you've designed?",
  "What would you redo with more time?",
  "What kind of projects make you say yes?",
  "Which project scared you the most (in a good way)?",

  // Personal/Philosophy
  "What's your work philosophy?",
  "What's your superpower?",
  "What motivates you?",
  "What's your learning approach?",
  "How do you reset when stuck?",
  "What's something you've unlearned?",
  "Are you a morning person or a night owl?",
  "What kind of music do you work to?",
  "Left-handed perks?",
  "Coffee order?",
  "Biggest non-design hobby?",
  "What keeps you up at night?",
  "Favourite failure?",
  "Best advice received?",
  "Worst advice ignored?",

  // Future/Exploratory
  "What do you think is next after screens?",
  "How do you use AI in your workflow?",
  "If you could redesign any product, what would it be?",
  "What's a product you wish you'd made?",
  "What would you do if you weren't a designer?",
  "What kind of problem do you want to solve next?",
  "Any personal projects on your wishlist?",
  "AR or VR future?",
  "Biggest tech red flag?",
  "Dream collaboration?",
  "Next skill to learn?",
  "Tool you wish existed?",
  "How would you fix email?",
  "What breaks design?",
  "Will screens disappear?",
  "Design trend to kill?",
  "Most exciting tech in 5 yrs?"
  ];

  // Function to get 3 random questions
  const getRandomQuestions = () => {
    // Get all user messages' content (case-insensitive)
    const asked = messages.filter(m => m.type === 'user').map(m => m.content.trim().toLowerCase());
    // Filter out questions that have already been asked
    const available = allQuickQuestions.filter(q => !asked.includes(q.trim().toLowerCase()));
    const shuffled = [...available].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };

  // Initialize random questions on component mount
  useEffect(() => {
    setQuickQuestions(getRandomQuestions());
  }, []);

  // Scroll to bottom only if typewriter is done or user sends a message
  useEffect(() => {
    if (!isTypewriting) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTypewriting]);

  // Detect new bot message and trigger typewriter only for new ones
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (
      lastMsg &&
      lastMsg.type === 'bot' &&
      lastMsg.id !== lastAnimatedBotId
    ) {
      setIsTypewriting(true);
      setLastAnimatedBotId(lastMsg.id);
    }
    // eslint-disable-next-line
  }, [messages]);

  // Update refreshQuestions and useEffect to use the new getRandomQuestions
  useEffect(() => {
    setQuickQuestions(getRandomQuestions());
    // eslint-disable-next-line
  }, [messages]);

  const refreshQuestions = () => {
    setQuickQuestions(getRandomQuestions());
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setInputMessage('');
    setIsLoading(true);

    // Agrega el mensaje del usuario inmediatamente
    setMessages(prev => [...prev, userMessage]);

    try {
      // Prepare conversation history for context
      const conversationHistory = messages
        .filter(msg => msg.type !== 'bot' || msg.id !== 1) // Exclude initial greeting
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        }));

      const response = await api.post('/api/chat', {
        message: userMessage.content,
        conversationHistory,
        conversationId
      });

      // El mensaje del bot se agrega después
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMsg = error.response?.data?.error || 'Sorry, I encountered an error. Please try again.';
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: errorMsg,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  // Check if there are any user messages
  const hasUserMessage = messages.some(m => m.type === 'user');

  // Scroll input into view on mobile after mount
  useEffect(() => {
    if (window.innerWidth <= 600) {
      setTimeout(() => {
        const input = document.querySelector('.input-container');
        if (input) input.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 500);
    }
  }, []);

  return (
    <div className="app">
      <a href="https://play.onealzate.com" target="_blank" rel="noopener noreferrer" className="logo-link">
        <img src="/JCAPLogo.svg" alt="JCAP Logo" className="logo-link-img" />
      </a>
      <div className="chat-outer-wrapper">
        {/* Header fuera del chat-container solo en desktop */}
        <div className="chat-header-outer desktop-only">
          {messages.length === 0 && (
            <div className="chat-subtitle">
              {subtitle}
            </div>
          )}
        </div>
        <div className={`chat-container${!hasUserMessage ? ' chat-container--compact' : ''}`}>
          <div className="messages-container">
            {/* Header dentro del chat-container solo en mobile */}
            {messages.length === 0 && (
              <div className="chat-header-outer mobile-only">
                <div className="chat-subtitle">
                  {subtitle}
                </div>
              </div>
            )}
            {messages.map((message, idx) => {
              const isFirstBot = idx === 0 && message.type === 'bot';
              const isLatestBot = message.type === 'bot' && idx === messages.length - 1;
              const shouldAnimate = isLatestBot && !animatedBotIds.current.has(message.id);
              return (
                <div
                  key={message.id}
                  className={`message ${message.type === 'user' ? 'user-message' : 'bot-message'}`}
                >
                  {!isFirstBot && (
                    <div className="message-avatar">
                      {message.type === 'user' ? (
                        <User size={20} />
                      ) : (
                        <img src="/juan-avatar.png" alt="AI Avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      )}
                    </div>
                  )}
                  <div className="message-content">
                    {isFirstBot ? (
                      <div className="message-text" style={{ color: '#888' }}>Ask anything...</div>
                    ) : message.type === 'bot' ? (
                      shouldAnimate ? (
                        <TypewriterMarkdown
                          content={message.content}
                          onDone={() => {
                            setIsTypewriting(false);
                            animatedBotIds.current.add(message.id);
                          }}
                        />
                      ) : (
                        <MarkdownRenderer content={message.content} />
                      )
                    ) : (
                      <div className="message-text">{message.content}</div>
                    )}
                    {!isFirstBot && (
                      <div className="message-timestamp">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {isLoading && (
              <div className="message bot-message">
                <div className="message-avatar">
                  <img src="/juan-avatar.png" alt="AI Avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                </div>
                <div className="message-content">
                  <div className="loading-indicator">
                    <Loader2 className="loading-icon" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <div className="quick-questions">
              <div className="quick-questions-header">
                <span>Quick questions:</span>
                <button 
                  onClick={refreshQuestions}
                  className="refresh-questions-btn"
                  disabled={isLoading || isTypewriting}
                  title="Get new questions"
                >
                  ↻
                </button>
              </div>
              <div className="quick-questions-list">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="quick-question-btn"
                    disabled={isLoading || isTypewriting}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            <div className="input-wrapper">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask anything..."
                disabled={isLoading || isTypewriting}
                rows={1}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading || isTypewriting}
                className="send-button"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat; 